import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Edit, Trash2, Search, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { company_name: "", vendor_name: "", gst_number: "", address: "", city: "", state: "", pin_code: "", phone: "", email: "", pan_number: "" };

const AccountsVendors = () => {
  const [vendors, setVendors] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({...emptyForm});
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  const fetch = useCallback(async () => {
    const r = await axios.get(`${API}/accounts/vendors`);
    setVendors(r.data);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    if (!form.company_name) { toast.error("Company name required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/accounts/vendors/${editing.id}`, form);
        toast.success("Vendor updated");
      } else {
        await axios.post(`${API}/accounts/vendors`, form);
        toast.success("Vendor created");
      }
      setShowDialog(false);
      setEditing(null);
      setForm({...emptyForm});
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    await axios.delete(`${API}/accounts/vendors/${id}`);
    toast.success("Deleted");
    fetch();
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({
      company_name: v.company_name || "", vendor_name: v.vendor_name || "",
      gst_number: v.gst_number || v.gstin || "", address: v.address || "",
      city: v.city || "", state: v.state || "", pin_code: v.pin_code || "",
      phone: v.phone || "", email: v.email || "", pan_number: v.pan_number || "",
    });
    setShowDialog(true);
  };

  const filtered = vendors.filter(v => {
    const q = search.toLowerCase();
    return v.company_name?.toLowerCase().includes(q) || v.gst_number?.toLowerCase().includes(q) || v.gstin?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="accounts-vendors-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-blue-400 flex items-center gap-2"><Building2 className="w-5 h-5" /> Vendor Database</h2>
          <p className="text-gray-400 text-sm">{vendors.length} vendors</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setForm({...emptyForm}); setShowDialog(true); }}
          className="bg-blue-600 hover:bg-blue-700" data-testid="add-vendor-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Vendor
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or GST..." className="bg-gray-800 border-gray-600 text-white pl-9 h-9" />
      </div>

      <Card className="bg-gray-900/30 border-gray-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700">
                <TableHead className="text-gray-400 w-8"></TableHead>
                <TableHead className="text-gray-400">Company Name</TableHead>
                <TableHead className="text-gray-400">GST Number</TableHead>
                <TableHead className="text-gray-400">City</TableHead>
                <TableHead className="text-gray-400">Phone</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">No vendors. Click "Add Vendor".</TableCell></TableRow>
              ) : filtered.map(v => (
                <React.Fragment key={v.id}>
                  <TableRow className="border-gray-800 hover:bg-gray-800/40 cursor-pointer" onClick={() => setExpanded(p => ({...p, [v.id]: !p[v.id]}))}>
                    <TableCell>{expanded[v.id] ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}</TableCell>
                    <TableCell className="text-white font-medium">{v.company_name}</TableCell>
                    <TableCell className="text-cyan-400 font-mono text-xs">{v.gst_number || v.gstin || "—"}</TableCell>
                    <TableCell className="text-gray-300">{v.city || "—"}</TableCell>
                    <TableCell className="text-gray-300">{v.phone || "—"}</TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8 px-2" onClick={() => openEdit(v)}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 px-2" onClick={() => handleDelete(v.id)}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                  {expanded[v.id] && (
                    <TableRow className="border-gray-800 bg-gray-900/50">
                      <TableCell colSpan={6} className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div><span className="text-gray-500">Contact:</span> <span className="text-gray-300 ml-1">{v.vendor_name || "—"}</span></div>
                          <div><span className="text-gray-500">Address:</span> <span className="text-gray-300 ml-1">{v.address || "—"}</span></div>
                          <div><span className="text-gray-500">State:</span> <span className="text-gray-300 ml-1">{v.state || "—"}</span></div>
                          <div><span className="text-gray-500">PIN:</span> <span className="text-gray-300 ml-1">{v.pin_code || "—"}</span></div>
                          <div><span className="text-gray-500">Email:</span> <span className="text-gray-300 ml-1">{v.email || "—"}</span></div>
                          <div><span className="text-gray-500">PAN:</span> <span className="text-gray-300 ml-1">{v.pan_number || "—"}</span></div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-blue-400">{editing ? "Edit" : "Add"} Vendor</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            <div><Label className="text-gray-300">Company Name *</Label><Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div><Label className="text-gray-300">Contact Person</Label><Input value={form.vendor_name} onChange={e => setForm({...form, vendor_name: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div><Label className="text-gray-300">GST Number</Label><Input value={form.gst_number} onChange={e => setForm({...form, gst_number: e.target.value})} placeholder="e.g., 27AABCT1234A1Z5" className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div><Label className="text-gray-300">PAN Number</Label><Input value={form.pan_number} onChange={e => setForm({...form, pan_number: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div><Label className="text-gray-300">Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-gray-300">City</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
              <div><Label className="text-gray-300">State</Label><Input value={form.state} onChange={e => setForm({...form, state: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
              <div><Label className="text-gray-300">PIN Code</Label><Input value={form.pin_code} onChange={e => setForm({...form, pin_code: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-gray-300">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
              <div><Label className="text-gray-300">Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsVendors;
